/**
 * Cours en voiture — Les 10 pré-requis pour conduire en sécurité.
 * Fiches basées sur le REMC (Référentiel pour l'Éducation à une Mobilité Citoyenne)
 * et les recommandations officielles de securite-routiere.gouv.fr.
 * Usage : fiches de référence pour l'enseignant ET l'élève, à consulter avant/après leçon.
 */

export const PREREQUIS_LIST = [
  {
    id: 1,
    slug: 'mecanique',
    icon: 'Wrench',
    accent: 'red',
    kicker: 'Pré-requis #1',
    title: 'Mécanique du véhicule',
    tagline: 'Connaître son outil de travail pour anticiper et réagir.',
    pourquoi: "Un conducteur qui comprend son véhicule anticipe les pannes, réagit correctement à un voyant, et adapte sa conduite (freinage ABS, ESP, boîte de vitesses). Sans cette base, tout le reste est fragile.",
    comment: [
      "Identifier les 3 familles de voyants : rouge (arrêt immédiat), orange (intervention rapide), vert/bleu (information).",
      "Comprendre le rôle du frein moteur : rétrograder = ralentir sans user les plaquettes, indispensable en descente.",
      "Savoir vérifier avant chaque départ : pression pneus, niveau lave-glace, éclairages actifs.",
      "Reconnaître les bruits anormaux (couinement plaquettes, cliquetis moteur, vibrations volant).",
    ],
    points_cles: [
      "Un voyant rouge = arrêt en sécurité immédiat",
      "Pneus sous-gonflés = distance de freinage x1.5",
      "Frein moteur avant frein à pédale en descente",
      "Contrôle visuel du véhicule : 30 secondes suffisent",
    ],
    erreurs: [
      "Ignorer un voyant orange \"parce que ça roule encore\"",
      "Utiliser uniquement la pédale de frein en descente (surchauffe)",
      "Ne pas savoir où est le triangle / gilet en cas de panne",
      "Confondre feu de position et feu de croisement",
    ],
    exercices: [
      "Tour complet du véhicule à voix haute avant le départ (VOSN : Vérifications au démarrage)",
      "Simulation panne : allumer le warning, sortir triangle et gilet, se positionner à l'abri",
      "En descente : maintenir vitesse constante uniquement au frein moteur",
    ],
    source_label: 'securite-routiere.gouv.fr',
    source_url: 'https://www.securite-routiere.gouv.fr/reglementation-generale/regles-generales-de-circulation/le-vehicule-terrestre-motorise',
  },
  {
    id: 2,
    slug: 'synchronisation',
    icon: 'GitBranch',
    accent: 'amber',
    kicker: 'Pré-requis #2',
    title: 'Synchronisation des membres',
    tagline: 'Pieds, mains et cerveau : la coordination fine qui rend la conduite fluide.',
    pourquoi: "Un élève qui n'a pas synchronisé embrayage-accélérateur-vitesse cale, patine, ou avance par à-coups. Cette compétence est la BASE mécanique de tout le reste : impossible de regarder loin si on lutte encore avec ses pieds.",
    comment: [
      "Débrayer AVANT de freiner en dessous de 30 km/h pour ne pas caler.",
      "Passer la vitesse en 2 temps : embrayage à fond, main droite ferme, embrayage relâché doucement.",
      "Point de patinage : sentir le moment où l'embrayage \"prend\" (le véhicule commence à avancer sans accélérer).",
      "Éviter de \"presser\" les deux pédales en même temps hors phase de démarrage.",
    ],
    points_cles: [
      "Embrayage = pied gauche uniquement, jamais utilisé pour freiner",
      "Le point de patinage se trouve toujours au même endroit sur une voiture donnée",
      "En manœuvre lente : 1ère + patinage, sans accélérer",
      "Passer les rapports SANS regarder le levier",
    ],
    erreurs: [
      "Regarder le levier de vitesses en passant les rapports",
      "Rester en 3ème à 30 km/h (moteur qui broute)",
      "Débrayer 50 m avant un stop (perte de contrôle moteur)",
      "Freiner brutalement sans débrayer et caler",
    ],
    exercices: [
      "Slalom à faible allure sans caler : test parfait de patinage",
      "Démarrage en côte 3 fois d'affilée sans reculer",
      "Passer de 2ème en 3ème sans regarder, corriger si mauvaise vitesse",
    ],
  },
  {
    id: 3,
    slug: 'regard',
    icon: 'Eye',
    accent: 'sky',
    kicker: 'Pré-requis #3',
    title: 'Le regard',
    tagline: 'Regarder loin, c\'est conduire tôt. L\'anticipation commence par les yeux.',
    pourquoi: "80% de l'information routière passe par les yeux. Un élève qui regarde à 5 m devant son capot subit la route, celui qui regarde à 200 m la construit. Le regard est LE prérequis qui débloque tous les autres.",
    comment: [
      "Placer le regard loin (200-300 m en rase campagne, 50-80 m en ville).",
      "Balayer : loin → moyen → près → rétros toutes les 5-10 secondes.",
      "En virage : regarder OÙ tu veux aller (le point de corde/sortie), pas où tu es.",
      "Passages piétons, écoles, sortie de garage : ralentir le regard, ralentir l'allure.",
    ],
    points_cles: [
      "Loin = anticipation, près = correction — jamais l'inverse",
      "Le regard précède TOUJOURS la trajectoire",
      "Un rétro toutes les 8-10 secondes minimum",
      "Devant un obstacle : je regarde OÙ je veux passer, pas l'obstacle",
    ],
    erreurs: [
      "Fixer le capot ou la ligne blanche juste devant",
      "Regarder l'obstacle qu'on veut éviter (on va dedans)",
      "Ne pas regarder les rétros en changeant de voie",
      "Se laisser hypnotiser par les feux stop du véhicule devant",
    ],
    exercices: [
      "En rase campagne, annoncer à voix haute ce que je vois à 200 m",
      "Passage devant école : compter les enfants visibles + ralentir",
      "En virage : viser le point de corde du regard AVANT de tourner le volant",
    ],
  },
  {
    id: 4,
    slug: 'trajectoire',
    icon: 'Route',
    accent: 'emerald',
    kicker: 'Pré-requis #4',
    title: 'Trajectoire',
    tagline: 'La bonne trajectoire économise le pneu, le carburant et surtout la vie.',
    pourquoi: "Une mauvaise trajectoire en virage = sortie de route ou empiétement sur la voie inverse. C'est la 2ème cause d'accidents mortels sur route départementale. Une trajectoire propre est aussi plus confortable pour les passagers.",
    comment: [
      "Entrée large, corde à l'apex, sortie large — sans jamais dépasser sa voie.",
      "Adapter la vitesse AVANT le virage (freinage en ligne droite, jamais en courbe).",
      "En virage serré, tenir le volant fermement et ne pas croiser les mains.",
      "Anticiper la sortie : dès qu'on voit la sortie, on peut ré-accélérer progressivement.",
    ],
    points_cles: [
      "Freiner AVANT le virage, accélérer APRÈS",
      "Ne jamais couper une ligne continue en virage",
      "Trajectoire économique = moins de fatigue passagers",
      "En 2 voies : rester dans SA voie, même si on \"pourrait\" prendre l'autre",
    ],
    erreurs: [
      "Freiner dans le virage (perte d'adhérence)",
      "Prendre la corde au-delà de sa voie (mordre sur voie inverse)",
      "Ne pas ré-accélérer en sortie (\"traîner\" en sortie de virage)",
      "Crisper les mains à 10h/2h en virage — mieux vaut 9h/3h",
    ],
    exercices: [
      "Route de campagne : commenter chaque virage (entrée, corde, sortie)",
      "Ralentir à 30 km/h dans un virage habitué à 50 : sensation d'aisance",
      "Filmer la trajectoire (caméra bord) et débriefer après",
    ],
    source_label: 'securite-routiere.gouv.fr',
    source_url: 'https://www.securite-routiere.gouv.fr/reglementation-generale/regles-generales-de-circulation/la-vitesse',
  },
  {
    id: 5,
    slug: 'placement',
    icon: 'Crosshair',
    accent: 'violet',
    kicker: 'Pré-requis #5',
    title: 'Placement sur la chaussée',
    tagline: 'Sur la route, ta place est précise. Elle t\'appartient — tu la défends sans hostilité.',
    pourquoi: "Un placement approximatif crée l'insécurité chez les autres usagers et te coupe des angles de vue. Bien placé, tu es prévisible et sûr — mal placé, tu deviens un piège pour les autres.",
    comment: [
      "En voie de droite en France (jamais à cheval).",
      "Distance latérale piétons/cyclistes : 1 m en ville, 1.5 m hors agglo.",
      "Voie de gauche = dépassement UNIQUEMENT, on revient à droite après.",
      "Aux intersections : se pré-positionner selon la direction (voie de droite pour tourner à droite, gauche pour tourner à gauche, milieu pour tout droit si voies multiples).",
    ],
    points_cles: [
      "1.5 m minimum d'un cycliste hors agglomération",
      "Voie de gauche = interdite hors dépassement",
      "Pré-signalisation d'intersection : voie choisie 100 m avant",
      "Ne jamais rouler sur une ligne discontinue \"pour être au milieu\"",
    ],
    erreurs: [
      "Rouler à cheval sur 2 voies \"parce que je vais tourner\"",
      "Serrer à droite quand un cycliste est là (pincement)",
      "Prendre la voie de gauche pour aller plus vite sans dépasser",
      "Se pré-positionner au dernier moment devant l'intersection",
    ],
    exercices: [
      "Sur autoroute : rester dans voie de droite pendant 10 min consécutives",
      "Ville : cycliste à droite → se déplacer sur voie centrale ou attendre",
      "Rond-point : voie extérieure pour sortie 1re, intérieure pour 3e ou plus",
    ],
    source_label: 'securite-routiere.gouv.fr',
    source_url: 'https://www.securite-routiere.gouv.fr/reglementation-generale/regles-generales-de-circulation/la-circulation-sur-la-chaussee',
  },
  {
    id: 6,
    slug: 'partage',
    icon: 'Users',
    accent: 'red',
    kicker: 'Pré-requis #6',
    title: 'Partage de la route',
    tagline: 'Piétons, cyclistes, deux-roues, poids lourds : chacun sa règle, tous ma responsabilité.',
    pourquoi: "Les usagers vulnérables (piétons, cyclistes, motards) représentent 45% des tués sur la route (ONISR 2024). L'élève doit apprendre à les VOIR AVANT de les CROISER, et à leur laisser leur espace vital.",
    comment: [
      "Piéton : priorité absolue sur passage, mais aussi engagé sur trottoir/refuge.",
      "Cycliste : dépassement à 1 m ville / 1.5 m hors agglo — sortir de sa voie si besoin.",
      "Moto : vérifier les angles morts 2× plus souvent (ils sont partout).",
      "Poids lourd : ne jamais rester dans leurs angles morts (arrière-droite surtout).",
    ],
    points_cles: [
      "Passage piéton : ralentir même si personne visible (l'enfant surgit)",
      "Cycliste : jamais de coup de klaxon inutile (déséquilibre)",
      "Moto : contrôle des rétros +épaule systématique",
      "Camion : si tu ne vois pas ses rétros, il ne te voit pas",
    ],
    erreurs: [
      "Klaxonner un cycliste \"pour signaler\"",
      "Serrer un piéton engagé sur un passage",
      "Ouvrir sa portière sans regarder (dooring — mortel pour cycliste)",
      "Suivre un poids lourd de trop près en montée",
    ],
    exercices: [
      "Ville dense : compter les cyclistes croisés en 15 min",
      "Passage piéton avec enfants : arrêt franc à 3 m, contact visuel",
      "Route étroite + cycliste : décider si on double ou si on attend",
    ],
    source_label: 'securite-routiere.gouv.fr',
    source_url: 'https://www.securite-routiere.gouv.fr/reglementation-generale/regles-generales-de-circulation/le-partage-de-la-route',
  },
  {
    id: 7,
    slug: 'intersections',
    icon: 'GitFork',
    accent: 'amber',
    kicker: 'Pré-requis #7',
    title: 'Intersections',
    tagline: '2 accidents mortels sur 3 en ville ont lieu à une intersection. Anticipe, identifie, agis.',
    pourquoi: "L'intersection est le lieu de croisement de trajectoires. Une erreur d'analyse = collision. L'élève doit apprendre à identifier le TYPE d'intersection (priorité, cédez-le-passage, stop, feux), lire les signaux, et décider en avance.",
    comment: [
      "Identifier à 100 m : type d'intersection + priorité (panneau, ligne au sol, feux).",
      "Aborder : rétrograder, ralentir, se placer selon direction, jeter un œil aux 2 côtés.",
      "Franchir : dans le doute, s'arrêter — mieux vaut un stop de trop.",
      "Rond-point : cédez à ceux qui sont dedans, clignotant à la sortie (jamais à l'entrée).",
    ],
    points_cles: [
      "STOP = arrêt PIEDS À TERRE (roues immobiles)",
      "Cédez-le-passage : ralentir + regarder + s'arrêter si nécessaire",
      "Feu orange : freiner si possible en sécurité",
      "Priorité à droite en zone urbaine si rien n'indique le contraire",
    ],
    erreurs: [
      "Roulement au STOP (les 4 roues doivent être immobiles)",
      "Regarder juste à gauche à un cédez-le-passage (oublier la droite)",
      "Griller un feu orange \"c'est bon je passais\"",
      "Clignotant d'entrée dans un rond-point (illégal et confusionnel)",
    ],
    exercices: [
      "Test intersection à voix haute : \"Type = ?, priorité = ?, mon action = ?\"",
      "10 stops d'affilée en simulation avec arrêt pieds à terre à chaque fois",
      "Rond-point 3 voies : entrée, changement de voie interne, sortie",
    ],
    source_label: 'securite-routiere.gouv.fr',
    source_url: 'https://www.securite-routiere.gouv.fr/reglementation-generale/regles-generales-de-circulation/les-regles-de-priorite',
  },
  {
    id: 8,
    slug: 'controles-retros',
    icon: 'ScanEye',
    accent: 'sky',
    kicker: 'Pré-requis #8',
    title: 'Contrôles rétros + angles morts',
    tagline: 'Ce qui n\'est pas contrôlé n\'existe pas. Règle "MMV" : Miroir–Manœuvre–Vérification.',
    pourquoi: "L'angle mort est responsable de nombreux accidents de changement de voie et de démarrage. Un contrôle rétro + coup d'œil à l'épaule prend 1 seconde et peut sauver une vie.",
    comment: [
      "Avant chaque manœuvre (changement voie, démarrage, sortie place) : rétro intérieur + rétro côté + coup d'œil épaule.",
      "En roulant, contrôle rétros toutes les 8-10 secondes minimum.",
      "Régler rétros AVANT de démarrer : intérieur cadre la lunette arrière entière, extérieurs montrent 20% carrosserie / 80% route.",
      "Piéton/cycliste : contrôle rétros ET épaule (angle mort surtout côté droit).",
    ],
    points_cles: [
      "MMV = Miroir → Manœuvre → Vérification",
      "Coup d'œil à l'épaule OBLIGATOIRE avant tout démarrage",
      "Rétros bien réglés = 90% de l'angle mort supprimé",
      "Camion : jusqu'à 4 angles morts (avant, droite, gauche, arrière)",
    ],
    erreurs: [
      "Changer de voie en regardant que le rétro intérieur",
      "Démarrer d'une place sans contrôle épaule (cycliste bien placé dans l'angle mort)",
      "Rétros mal réglés (voir 50% capable ou capot au lieu de la route)",
      "Ouvrir portière côté chaussée sans vérifier arrière (dooring)",
    ],
    exercices: [
      "Sur départementale : contrôle rétros à voix haute toutes les 10s pendant 5 min",
      "Simulation cycliste dans l'angle mort : refaire le geste MMV 5 fois d'affilée",
      "Sortie de stationnement en ligne : rétros + épaule + clignotant + départ",
    ],
    source_label: 'securite-routiere.gouv.fr',
    source_url: 'https://www.securite-routiere.gouv.fr/lecole-de-la-route/les-angles-morts',
  },
  {
    id: 9,
    slug: 'allure',
    icon: 'Gauge',
    accent: 'emerald',
    kicker: 'Pré-requis #9',
    title: 'L\'allure',
    tagline: 'La bonne vitesse n\'est pas la vitesse autorisée : c\'est la vitesse adaptée.',
    pourquoi: "La vitesse est LE facteur commun de 1 accident mortel sur 3 (ONISR). L'élève apprend souvent \"la limite maximale\" mais doit apprendre \"la vitesse adaptée\" — celle qui tient compte de la météo, du trafic, de la visibilité, de son état.",
    comment: [
      "Limite autorisée = plafond, jamais objectif.",
      "Pluie : -20 km/h / Brouillard 50 m : 50 km/h max.",
      "Nuit : ne jamais rouler plus vite que ta capacité à t'arrêter dans l'éclairage des feux.",
      "Distance de sécurité : règle des 2 secondes (1 trait blanc = 39 m à 130 km/h).",
    ],
    points_cles: [
      "Distance de sécurité = 2 secondes minimum",
      "Pluie : diviser autonomie de freinage par 1.5",
      "Brouillard <50m : vitesse max = 50 km/h partout",
      "Nuit sans éclairage : ne pas rouler plus vite que ton faisceau",
    ],
    erreurs: [
      "Rouler à la limite MAX en conditions dégradées",
      "Coller le véhicule devant \"pour empêcher les autres de me couper\"",
      "Accélérer sur le vert d'un feu quand le trafic ralentit devant",
      "Confondre \"limite\" et \"vitesse minimale\"",
    ],
    exercices: [
      "Calcul distance sécurité à voix haute en fonction de la vitesse",
      "Sous la pluie légère : régler cruise à -10 par rapport à d'habitude",
      "Autoroute : maintenir 2 secondes derrière véhicule devant",
    ],
    source_label: 'securite-routiere.gouv.fr',
    source_url: 'https://www.securite-routiere.gouv.fr/reglementation-generale/regles-generales-de-circulation/les-limitations-de-vitesse',
  },
  {
    id: 10,
    slug: 'depassements',
    icon: 'Repeat',
    accent: 'violet',
    kicker: 'Pré-requis #10',
    title: 'Les dépassements',
    tagline: 'Le dépassement est un moment de vulnérabilité maximum. La règle : je vois, je suis vu, je décide.',
    pourquoi: "1 accident mortel hors agglo sur 5 se produit lors d'un dépassement (ONISR). C'est un moment où on est sur la voie inverse, exposé au trafic en face — la moindre erreur ne pardonne pas.",
    comment: [
      "Voir : visibilité minimum 3-4× la distance de dépassement (400 m à 90 km/h).",
      "Être vu : clignotant AVANT de déboîter, jamais après.",
      "Décider : possibilité de renoncer à tout moment tant qu'on n'a pas dépassé.",
      "Se rabattre : ne pas se rabattre trop tôt (voir le véhicule dépassé dans le rétro intérieur COMPLET).",
    ],
    points_cles: [
      "Visibilité 400 m minimum avant dépassement à 90 km/h",
      "Ligne continue = dépassement INTERDIT",
      "Cycliste : 1.5 m d'écart, franchir ligne continue autorisé si sécurité",
      "Se rabattre : quand on voit le véhicule dépassé dans le rétro entier",
    ],
    erreurs: [
      "Dépasser en côte ou virage (visibilité insuffisante)",
      "Rabattement trop tôt (queue de poisson)",
      "Dépassement à proximité d'une intersection",
      "Cligno après avoir déboîté (\"faux clignotant\")",
    ],
    exercices: [
      "Départementale avec ligne discontinue : identifier zones \"visible sur 400 m\"",
      "Dépasser cycliste : sortir sur voie inverse propre, se rabattre après 3 sec",
      "Renoncement : s'entraîner à annuler un dépassement engagé (retour derrière)",
    ],
    source_label: 'securite-routiere.gouv.fr',
    source_url: 'https://www.securite-routiere.gouv.fr/reglementation-generale/regles-generales-de-circulation/le-depassement',
  },
];
